/**
 * GitHub App API Service
 *
 * Provides API methods for managing GitHub App installations
 * and proxying GitHub API calls through our backend.
 *
 * @module services/githubAppApi
 */

import { API_BASE, getAuthHeaders, handleResponse as handleApiResponse } from './apiClient';

/**
 * GitHub App configuration
 */
export const GITHUB_APP_CONFIG = {
  appName: process.env.REACT_APP_GITHUB_APP_NAME || 'portfolistic',
  appId: process.env.REACT_APP_GITHUB_APP_ID,
  clientId: process.env.REACT_APP_GITHUB_APP_CLIENT_ID,
  installUrl: `https://github.com/apps/${process.env.REACT_APP_GITHUB_APP_NAME || 'portfolistic'}/installations/new`,
  configureUrl: (installationId) =>
    `https://github.com/settings/installations/${installationId}`,
};

/**
 * Get the installation URL for the GitHub App
 */
export const getInstallUrl = ({ target, suggestedRepos, redirectUri } = {}) => {
  const baseUrl = GITHUB_APP_CONFIG.installUrl;
  const params = new URLSearchParams();

  if (target) {
    params.set('suggested_target_id', target);
  }
  if (suggestedRepos?.length) {
    params.set('repository_ids', suggestedRepos.join(','));
  }
  if (redirectUri) {
    params.set('redirect_uri', redirectUri);
  }

  const query = params.toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
};

/**
 * Start a GitHub App installation by fetching a CSRF-state-protected redirect URL from the backend.
 * Returns { redirect_url } which the caller should navigate to.
 */
export const startInstallation = async (user) => {
  const response = await fetch(`${API_BASE}/github-app/install`, {
    headers: await getAuthHeaders(user),
  });
  return handleApiResponse(response);
};

/**
 * Get all GitHub App installations for the current user
 */
export const getInstallations = async (user) => {
  const response = await fetch(`${API_BASE}/github-app/installations`, {
    headers: await getAuthHeaders(user),
  });
  return handleApiResponse(response);
};

/**
 * Get details for a specific installation
 */
export const getInstallation = async (user, installationId) => {
  const response = await fetch(
    `${API_BASE}/github-app/installations/${installationId}`,
    { headers: await getAuthHeaders(user) }
  );
  return handleApiResponse(response);
};

/**
 * Get repositories accessible through an installation
 */
export const getInstallationRepos = async (user, installationId, { page = 1, perPage = 30 } = {}) => {
  const params = new URLSearchParams({ page, per_page: perPage });
  const response = await fetch(
    `${API_BASE}/github-app/installations/${installationId}/repos?${params}`,
    { headers: await getAuthHeaders(user) }
  );
  return handleApiResponse(response);
};

/**
 * Handle GitHub App installation callback
 */
export const handleInstallationCallback = async (user, { installationId, setupAction }) => {
  const response = await fetch(`${API_BASE}/github-app/callback`, {
    method: 'POST',
    headers: await getAuthHeaders(user),
    body: JSON.stringify({
      installation_id: installationId,
      setup_action: setupAction,
    }),
  });
  return handleApiResponse(response);
};

/**
 * Refresh an installation token
 */
export const refreshInstallationToken = async (user, installationId) => {
  const response = await fetch(
    `${API_BASE}/github-app/installations/${installationId}/token`,
    {
      method: 'POST',
      headers: await getAuthHeaders(user),
    }
  );
  return handleApiResponse(response);
};

/**
 * Remove an installation
 */
export const removeInstallation = async (user, installationId) => {
  const response = await fetch(
    `${API_BASE}/github-app/installations/${installationId}`,
    {
      method: 'DELETE',
      headers: await getAuthHeaders(user),
    }
  );
  return handleApiResponse(response);
};

/**
 * Sync installation repositories with GitHub
 */
export const syncInstallationRepos = async (user, installationId) => {
  const response = await fetch(
    `${API_BASE}/github-app/installations/${installationId}/sync`,
    {
      method: 'POST',
      headers: await getAuthHeaders(user),
    }
  );
  return handleApiResponse(response);
};

/**
 * Get organization access preferences
 */
export const getOrganizationAccess = async (user, platform = null) => {
  const params = platform ? `?platform=${platform}` : '';
  const response = await fetch(
    `${API_BASE}/organizations/access${params}`,
    { headers: await getAuthHeaders(user) }
  );
  return handleApiResponse(response);
};

/**
 * Update organization access preference
 */
export const updateOrganizationAccess = async (user, platform, orgId, updates) => {
  const response = await fetch(
    `${API_BASE}/organizations/access/${platform}/${orgId}`,
    {
      method: 'PATCH',
      headers: await getAuthHeaders(user),
      body: JSON.stringify(updates),
    }
  );
  return handleApiResponse(response);
};

/**
 * Sync all organizations from a platform
 */
export const syncOrganizations = async (user, platform) => {
  const response = await fetch(
    `${API_BASE}/organizations/sync/${platform}`,
    {
      method: 'POST',
      headers: await getAuthHeaders(user),
    }
  );
  return handleApiResponse(response);
};

// =========================================================================
// Proxy Methods - All GitHub API calls go through our backend
// =========================================================================

/**
 * Get repositories via backend proxy
 * @param {Object} user - Firebase user object
 * @param {number} installationId - GitHub App installation ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} { repositories, total_count, page, has_next }
 */
export const getProxyRepos = async (user, installationId, { page = 1, perPage = 30, search = '' } = {}) => {
  const params = new URLSearchParams({
    installation_id: installationId,
    page,
    per_page: perPage,
  });
  if (search) {
    params.set('search', search);
  }
  const response = await fetch(
    `${API_BASE}/github-app/proxy/repos?${params}`,
    { headers: await getAuthHeaders(user) }
  );
  return handleApiResponse(response);
};

/**
 * Get detailed repo info via backend proxy
 * @param {Object} user - Firebase user object
 * @param {string} owner - Repository owner
 * @param {string} repo - Repository name
 * @returns {Promise<Object>} { repo, languages, topics, has_readme, contributors_count }
 */
export const getProxyRepoDetails = async (user, owner, repo) => {
  const response = await fetch(
    `${API_BASE}/github-app/proxy/repos/${owner}/${repo}`,
    { headers: await getAuthHeaders(user) }
  );
  return handleApiResponse(response);
};

/**
 * Batch fetch repo details via backend proxy
 * @param {Object} user - Firebase user object
 * @param {string[]} repos - Array of "owner/repo" strings
 * @returns {Promise<Object>} { results, errors }
 */
export const getProxyRepoBatch = async (user, repos) => {
  const response = await fetch(
    `${API_BASE}/github-app/proxy/repos/batch`,
    {
      method: 'POST',
      headers: await getAuthHeaders(user),
      body: JSON.stringify({ repos }),
    }
  );
  return handleApiResponse(response);
};

/**
 * Get GitHub user profile via backend proxy
 * @param {Object} user - Firebase user object
 * @returns {Promise<Object>} { login, account_type, avatar_url, installations }
 */
export const getGitHubProfile = async (user) => {
  const response = await fetch(
    `${API_BASE}/github-app/proxy/user`,
    { headers: await getAuthHeaders(user) }
  );
  return handleApiResponse(response);
};

/**
 * Check if user needs to install GitHub App.
 * With full OAuth removal, this simply checks for installations.
 */
export const needsGitHubAppInstall = (user, installations = []) => {
  return installations.length === 0;
};

/**
 * Get GitHub App benefits for display
 */
export const getGitHubAppBenefits = () => [
  {
    title: 'Organization Access',
    description: 'Access repositories from organizations you belong to',
    icon: 'building2',
  },
  {
    title: 'Fine-grained Permissions',
    description: 'Grant access only to specific repositories',
    icon: 'shield-check',
  },
  {
    title: 'No Personal Token Needed',
    description: 'Works without storing your personal access token',
    icon: 'key',
  },
  {
    title: 'Automatic Updates',
    description: 'Permissions stay in sync with your GitHub settings',
    icon: 'refresh-cw',
  },
];

export default {
  startInstallation,
  getInstallUrl,
  getInstallations,
  getInstallation,
  getInstallationRepos,
  handleInstallationCallback,
  refreshInstallationToken,
  removeInstallation,
  syncInstallationRepos,
  getOrganizationAccess,
  updateOrganizationAccess,
  syncOrganizations,
  getProxyRepos,
  getProxyRepoDetails,
  getProxyRepoBatch,
  getGitHubProfile,
  needsGitHubAppInstall,
  getGitHubAppBenefits,
  GITHUB_APP_CONFIG,
};
